import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef } from "@angular/core";
import { CommonModule, DatePipe } from "@angular/common";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import {
  NbCardModule,
  NbButtonModule,
  NbSpinnerModule,
  NbIconModule,
  NbDialogService,
  NbToastrService,
  NbAccordionModule,
  NbSelectModule,
  NbOptionModule
} from "@nebular/theme";
import { KPI } from "../../../../@core/api";
import { ComplianceService } from "../../../../@core/utils/compliance.service";
import { APIService } from "../../../../@core/api";
import { SaveToastService } from "../../../../@core/utils";
import {  } from "../../gauge-spinner/gauge-spinner.component";
import { ControlReportDialogComponent } from "../../control-report-dialog/control-report-dialog.component";
import { ConfirmationDialogComponent } from "../../confirmation-dialog/confirmation-dialog.component";
import { AddReportMethodDialogComponent } from "../../add-report-method-dialog/add-report-method-dialog.component";
import { ImportReportCodeDialogComponent } from "../../import-report-code-dialog/import-report-code-dialog.component";
import { map, catchError, timeout, switchMap } from "rxjs/operators";
import { of, forkJoin, Observable, Subscription } from "rxjs";

const RESERVATION_STATUS = {
  ACTIVE: "active",
  CLOSED: "closed",
  ABANDONED: "abandoned"
} as const;

const RESERVATION_CRITICALITY = {
  CRITICAL: "critical",
  NON_CRITICAL: "non_critical"
} as const;

const RESERVATION_ALERT_TYPES = [
  "Perimeter Reservation",
  "Réservation de périmètre",
  "perimeter_reservation",
  "reserve"
] as const;

@Component({
  selector: "app-site-control-reports",
  templateUrl: "./site-control-reports.component.html",
  styleUrls: ["./site-control-reports.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FontAwesomeModule,
    NbCardModule,
    NbButtonModule,
    NbSpinnerModule,
    NbIconModule,
    NbAccordionModule,
    NbSelectModule,
    NbOptionModule,
    
    DatePipe
  ]
})
export class SiteControlReportsComponent implements OnInit, OnDestroy {
  @Input() perimeterId: number;

  public reports: KPI.ControlOfficeReport[] = [];
  public reservations: KPI.Reservation[] = [];
  public filteredReservations: KPI.Reservation[] = [];
  public loading: boolean = true;
  public loadingReservations: boolean = true;
  public reservesExpanded: boolean = false;
  public reportsExpanded: boolean = true;
  public statusFilter: string = RESERVATION_STATUS.ACTIVE;
  public showAllStatuses: boolean = false;
  private subscriptions: Subscription = new Subscription();
  private readonly STORAGE_KEY = "site-control-reports-expanded-state";

  constructor(
    private complianceService: ComplianceService,
    private apiService: APIService,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private datePipe: DatePipe,
    private saveToast: SaveToastService
  ) {}

  ngOnInit(): void {
    this.initializeExpandedState();
    this.loadData();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private loadData(): void {
    this.loading = true;
    this.loadingReservations = true;

    this.subscriptions.add(
      forkJoin({
        reports: this.loadReports(),
        alerts: this.loadReservationsFromAlerts()
      })
        .pipe(timeout(10000))
        .subscribe({
          next: ({ reports, alerts }) => {
            this.reports = reports;
            this.reservations = alerts;
            this.applyFilters();
            this.loading = false;
            this.loadingReservations = false;
            this.cdr.detectChanges();
          },
          error: error => {
            console.error("Error loading data:", error);
            this.loading = false;
            this.loadingReservations = false;
            this.reservations = [];
            this.filteredReservations = [];
            this.cdr.detectChanges();
          }
        })
    );
  }

  private loadReports(): Observable<KPI.ControlOfficeReport[]> {
    return this.complianceService
      .listControlOfficeReports({
        perimeter: this.perimeterId
      })
      .pipe(
        map(reports => {
          return reports.sort((a, b) => {
            const dateA = new Date(a.date).getTime();
            const dateB = new Date(b.date).getTime();
            return dateB - dateA;
          });
        }),
        catchError(() => of([]))
      );
  }

  private loadReservationsFromAlerts(): Observable<KPI.Reservation[]> {
    const reservationStatus = (!this.showAllStatuses && this.statusFilter === RESERVATION_STATUS.ACTIVE) 
      ? RESERVATION_STATUS.ACTIVE 
      : undefined;
    
    return this.apiService.getAlertsTable(this.perimeterId, reservationStatus).pipe(
      timeout(10000),
      map((response: any) => {
        if (response && response.alerts && Array.isArray(response.alerts)) {
          const filtered = response.alerts.filter(
            (alert: any) => {
              const matchesType = RESERVATION_ALERT_TYPES.includes(alert.type as any);
              const matchesAlertType = RESERVATION_ALERT_TYPES.includes(alert.alert_type as any);
              const matchesSource = RESERVATION_ALERT_TYPES.includes(alert.source as any);
              const idContainsReservation = alert.id && alert.id.includes('reservation_');
              const hasReservationId = alert.reservation_id !== undefined && alert.reservation_id !== null;
              
              return matchesType || matchesAlertType || matchesSource || idContainsReservation || hasReservationId;
            }
          );
          
          const mapped = filtered.map(
            (alert: any) => {
              let reservationId = alert.reservation_id;
              if (!reservationId && alert.id && alert.id.includes('reservation_')) {
                const match = alert.id.match(/reservation_(\d+)/);
                if (match) {
                  reservationId = parseInt(match[1]);
                }
              }
              
              let criticality = alert.criticality;
              if (!criticality && alert.criticality_color) {
                const colorMap: { [key: string]: string } = {
                  'red': 'critical',
                  'orange': 'non_critical',
                };
                criticality = colorMap[alert.criticality_color.toLowerCase()] || alert.criticality_color;
              }
              
              const reservation = {
                id: reservationId || parseInt(alert.id?.split("_")?.pop() || "0") || alert.id,
                label: alert.label || '',
                status: alert.status,
                issue_date: alert.issue_date,
                criticality: criticality,
                linked_equipment: alert.linked_equipment,
                responsible: alert.responsible,
                closing_date: alert.closing_date,
                precision: alert.precision,
                comment: alert.comment,
                source: alert.source,
                control_office_report: alert.control_office_report,
                compliance: alert.compliance_id || (alert.id?.match(/compliance_(\d+)/)?.[1] ? parseInt(alert.id.match(/compliance_(\d+)/)[1]) : undefined) || alert.compliance,
                perimeter: alert.linked_perimeter_id || this.perimeterId
              } as KPI.Reservation;
              
              return reservation;
            }
          );
          
          return mapped;
        }
        return [];
      }),
      catchError(error => {
        console.error("Error loading reservations from alerts:", error);
        return of([]);
      })
    );
  }

  private applyFilters(): void {
    this.filteredReservations = this.reservations;
  }

  private initializeExpandedState(): void {
    const savedState = localStorage.getItem(this.STORAGE_KEY);
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        this.reservesExpanded = state.reservesExpanded !== undefined ? state.reservesExpanded : false;
        this.reportsExpanded = state.reportsExpanded !== undefined ? state.reportsExpanded : true;
      } catch (error) {
        console.error("Error parsing saved expanded state:", error);
        this.reservesExpanded = false;
        this.reportsExpanded = true;
      }
    } else {
      this.reservesExpanded = false;
      this.reportsExpanded = true;
    }
  }

  private saveExpandedState(): void {
    const state = {
      reservesExpanded: this.reservesExpanded,
      reportsExpanded: this.reportsExpanded
    };
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
  }

  public onReservesCollapsedChange(collapsed: boolean): void {
    this.reservesExpanded = !collapsed;
    this.saveExpandedState();
  }

  public onReportsCollapsedChange(collapsed: boolean): void {
    this.reportsExpanded = !collapsed;
    this.saveExpandedState();
  }

  public clearStatusFilter(): void {
    this.showAllStatuses = true;
    this.statusFilter = "";
    this.loadData();
  }

  public onFilterInfoClick(event: Event): void {
    event.stopPropagation();
    this.clearStatusFilter();
  }

  public formatDate(dateString: string, useShortYear: boolean = true): string {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      const format = useShortYear ? "d MMM yy" : "d MMM yyyy";
      const formatted = this.datePipe.transform(date, format, "fr-FR") || "-";
      return formatted.replace(/\s+/g, " ").trim();
    } catch (error) {
      return "-";
    }
  }

  public openAddReportDialog(): void {
    const methodDialog = this.dialogService.open(AddReportMethodDialogComponent, {
      closeOnBackdropClick: true,
      hasScroll: false
    });

    this.subscriptions.add(
      methodDialog.onClose.subscribe((method: 'automatic' | 'manual' | null) => {
        if (method === 'automatic') {
          this.openImportCodeDialog();
        } else if (method === 'manual') {
          this.openManualEntryDialog();
        }
      })
    );
  }

  private openImportCodeDialog(): void {
    const dialogRef = this.dialogService.open(ImportReportCodeDialogComponent, {
      autoFocus: false,
      closeOnBackdropClick: false,
      hasScroll: false,
      hasBackdrop: true,
      backdropClass: 'control-report-backdrop',
      dialogClass: 'control-report-dialog-overlay'
    });

    this.subscriptions.add(
      dialogRef.onClose.subscribe((reportCode: string | null) => {
        if (reportCode) {
          this.loadData();
        }
      })
    );
  }

  private openManualEntryDialog(): void {
    const dialogRef = this.dialogService.open(ControlReportDialogComponent, {
      autoFocus: false,
      closeOnBackdropClick: false,
      hasScroll: false,
      hasBackdrop: true,
      backdropClass: 'control-report-backdrop',
      dialogClass: 'control-report-dialog-overlay',
      context: {
        reportId: null,
        asset: null,
        perimeterId: this.perimeterId,
        importMode: 'manual',
        onReportSaved: () => {
          this.loadData();
        }
      } as any
    });

    this.subscriptions.add(
      dialogRef.onClose.subscribe(() => {
        this.loadData();
      })
    );
  }

  public openEditReportDialog(report: KPI.ControlOfficeReport): void {
    const dialogRef = this.dialogService.open(ControlReportDialogComponent, {
      autoFocus: false,
      closeOnBackdropClick: false,
      hasScroll: false,
      hasBackdrop: true,
      backdropClass: 'control-report-backdrop',
      dialogClass: 'control-report-dialog-overlay',
      context: {
        reportId: report.id,
        asset: null,
        perimeterId: this.perimeterId,
        onReportSaved: () => {
          this.loadData();
        }
      } as any
    });

    this.subscriptions.add(
      dialogRef.onClose.subscribe(() => {
        this.loadData();
      })
    );
  }

  public deleteReport(report: KPI.ControlOfficeReport): void {
    const title = this.translate.instant("site_control_reports.delete_report_title");
    const message = this.translate.instant("site_control_reports.delete_report_message");

    this.subscriptions.add(
      this.dialogService
        .open(ConfirmationDialogComponent, {
          context: {
            title: title,
            label: message
          }
        })
        .onClose.subscribe((confirmed: boolean) => {
          if (confirmed) {
            this.subscriptions.add(
              this.complianceService.getReportReservations(report.id).pipe(
                map((reservations: KPI.Reservation[]) => {
                  const reservationIds = reservations
                    .filter(reservation => reservation.id)
                    .map(reservation => reservation.id);
                  return reservationIds;
                }),
                switchMap((reservationIds: number[]) => {
                  if (reservationIds.length > 0) {
                    return this.complianceService.bulkDeleteReservations(reservationIds).pipe(
                      catchError(error => {
                        console.error("Error deleting reservations:", error);
                        return of(null);
                      })
                    );
                  }
                  return of(null);
                }),
                switchMap(() => {
                  return this.complianceService.deleteControlOfficeReport(report.id);
                })
              ).subscribe({
                next: () => {
                  this.saveToast.showSaved();
                  this.loadData();
                },
                error: error => {
                  console.error("Error deleting report:", error);
                  this.toastrService.danger(
                    this.translate.instant("site_control_reports.report_delete_error"),
                    this.translate.instant("Error")
                  );
                }
              })
            );
          }
        })
    );
  }

  public getReservationStatusLabel(status: string): string {
    if (!status) return "-";
    const statusLower = status.toLowerCase();
    const statusMap: { [key: string]: string } = {
      [RESERVATION_STATUS.ACTIVE]: this.translate.instant("site_control_reports.status_active"),
      [RESERVATION_STATUS.CLOSED]: this.translate.instant("site_control_reports.status_closed"),
      [RESERVATION_STATUS.ABANDONED]: this.translate.instant("site_control_reports.status_abandoned"),
      "clôturée": this.translate.instant("site_control_reports.status_closed"),
      "abandonnée": this.translate.instant("site_control_reports.status_abandoned")
    };
    return statusMap[statusLower] || status;
  }

  public getReservationCriticalityClass(criticality: string): string {
    if (!criticality) return "";
    const critLower = criticality.toLowerCase();
    if (critLower === RESERVATION_CRITICALITY.CRITICAL || critLower === "critical") {
      return "critical";
    }
    if (critLower === RESERVATION_CRITICALITY.NON_CRITICAL || critLower === "non_critical" || critLower === "non-critical") {
      return "non-critical";
    }
    if (critLower === "red" || critLower.includes("critique")) {
      return "critical";
    }
    return "non-critical";
  }

  public getCriticalityLabel(criticality: string): string {
    if (!criticality) return "-";
    const critLower = criticality.toLowerCase();
    if (critLower === RESERVATION_CRITICALITY.CRITICAL || critLower === "critical") {
      return this.translate.instant("site_control_reports.criticality_critical");
    }
    if (critLower === RESERVATION_CRITICALITY.NON_CRITICAL || critLower === "non_critical" || critLower === "non-critical") {
      return this.translate.instant("site_control_reports.criticality_non_critical");
    }
    if (critLower === "red" || critLower.includes("critique")) {
      return this.translate.instant("site_control_reports.criticality_critical");
    }
    return this.translate.instant("site_control_reports.criticality_non_critical");
  }

  public openEditReservationDialog(reservation: KPI.Reservation): void {
    let reportId: number | null = null;
    
    const controlOfficeReport = reservation.control_office_report;
    if (controlOfficeReport) {
      if (typeof controlOfficeReport === 'number') {
        reportId = controlOfficeReport;
      } else if (typeof controlOfficeReport === 'object' && controlOfficeReport !== null) {
        const reportObj = controlOfficeReport as any;
        if (reportObj.id !== undefined && reportObj.id !== null) {
          reportId = typeof reportObj.id === 'number' ? reportObj.id : parseInt(reportObj.id);
        }
      }
    }
    
    if (reportId) {
      const dialogRef = this.dialogService.open(ControlReportDialogComponent, {
        autoFocus: false,
        closeOnBackdropClick: false,
        hasScroll: false,
        hasBackdrop: true,
        backdropClass: 'control-report-backdrop',
        dialogClass: 'control-report-dialog-overlay',
        context: {
          reportId: reportId,
          asset: null,
          perimeterId: this.perimeterId,
          reservationId: reservation.id,
          onReportSaved: () => {
            this.loadData();
          }
        } as any
      });

      this.subscriptions.add(
        dialogRef.onClose.subscribe(() => {
          this.loadData();
        })
      );
    } else {
      this.toastrService.info(
        this.translate.instant("site_control_reports.edit_reservation_info"),
        this.translate.instant("Info")
      );
    }
  }

  public onReservationStatusChange(reservation: KPI.Reservation, newStatus: string): void {
    if (!reservation.id || reservation.status === newStatus) {
      return;
    }

    const updatePayload: KPI.ReservationUpdatePayload = {
      status: newStatus
    };

    if (newStatus === RESERVATION_STATUS.CLOSED) {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      updatePayload.closing_date = todayStr;
    }

    this.complianceService.updateReservation(reservation.id, updatePayload).pipe(
      catchError(() => {
        this.toastrService.danger(
          this.translate.instant("site_control_reports.error_updating_reservation"),
          this.translate.instant("Error")
        );
        return of(null);
      })
    ).subscribe((updatedReservation) => {
      if (updatedReservation) {
        this.saveToast.showSaved();
        this.loadData();
      }
    });
  }
}
